/**
 * L'etichetta con cui si mostra una certificazione.
 *
 * Il Gateway non scrive una sola forma per la stessa versione MRSL: la stessa
 * `v3.1` arriva come `ZDHC MRSL v3.1`, come `v3.1` o come `3.1` a seconda di
 * dove la si legge. La derivazione lato api la scrive **com'e' arrivata** — non
 * si normalizza in scrittura, la tolleranza sui dialetti sta in lettura — e il
 * risultato e' che una riga derivata oggi si legge `3.1`, che da sola non dice
 * nemmeno di quale schema si stia parlando.
 *
 * Qui si riconosce il dialetto e si mostra la forma canonica, che e' la stessa
 * che portano le righe strutturate legacy: la presentazione uniforma cio' che
 * il dato lascia in tre forme.
 *
 * ## Cio' che non si riconosce non si tocca
 *
 * Le stesse tabelle portano certificazioni che non sono affatto MRSL — la
 * forma ricca del Gateway ne dichiarava di ogni tipo — e riscriverle
 * significherebbe mostrare un'etichetta che nessuno ha prodotto. Il valore
 * grezzo e' sempre l'esito prudente: si torna quello, intatto.
 *
 * Per lo stesso motivo il riconoscimento e' **piu' stretto** di quello che l'api
 * usa per decidere se derivare (`MrslVersion::belongsToKnownGroup()`, che
 * confronta per prefisso perche' serve a costruire dei `LIKE`): qui il valore
 * deve essere una versione e nient'altro. Un ipotetico `3.0 Standard`
 * passerebbe il confronto per prefisso e diventerebbe `ZDHC MRSL v3.0 Standard`,
 * cioe' proprio l'etichetta inventata che si vuole evitare.
 */

/**
 * Il prefisso della forma canonica, come lo scrive `MrslVersion` sull'api.
 */
const CANONICAL_PREFIX = 'ZDHC MRSL ';

/**
 * Le versioni maggiori che una banda dei grafici rivendica, da
 * `MrslVersion::groups()` (`ZDHC MRSL v3.%` piu' `ZDHC MRSL v2.%`).
 *
 * Sono qui in copia perche' l'originale sta in PHP e non c'e' un posto solo che
 * i due possano leggere: aggiungere un gruppo di la' vuol dire aggiungerlo qui,
 * altrimenti la versione nuova si continua a mostrare grezza. Il costo di
 * dimenticarsene e' quello, non un'etichetta sbagliata.
 */
const KNOWN_MAJOR_VERSIONS = ['3', '2'];

/**
 * Le tre forme in cui la stessa versione puo' presentarsi, e nient'altro:
 * `ZDHC MRSL v3.1`, `v3.1`, `3.1`.
 */
const MRSL_VERSION = /^(?:ZDHC MRSL\s+)?v?(\d+)\.(\d+)$/i;

/**
 * @param  {*} raw il `certification` come arriva dall'api
 * @return {*} la forma canonica se il valore e' una versione MRSL nota, il
 *             valore di partenza intatto in ogni altro caso
 */
export const formatCertificationName = (raw) =>
{
    if(typeof raw !== 'string') return raw;

    const match = MRSL_VERSION.exec(raw.trim());

    if(!match) return raw;

    const [, major, minor] = match;

    if(!KNOWN_MAJOR_VERSIONS.includes(major)) return raw;

    return `${CANONICAL_PREFIX}v${major}.${minor}`;
};
